/* global BigInt */
// Serial event vocabulary (firmware v0.5.70 `ui on`). Every async event is one
// JSON line `{"t":…}`; this decodes the documented families into a category (for
// colour-coding) and a friendly one-line summary (for the decoded terminal
// view). Prose / non-JSON lines fall back to a string-based classifier.
//
// Families: session · button · ble · sync · impact · store · warning, plus the
// existing ack/nack and the manufacturing status/led/id lines.

// Parse a line to an object only if it looks like NDJSON. Returns null for
// prose (the UI's rule: JSON.parse lines starting with `{`, ignore the rest).
export const parseSerialLine = (data) => {
  const s = String(data == null ? "" : data).trimStart();
  if (s[0] !== "{") return null;
  try {
    const o = JSON.parse(s);
    return o && typeof o === "object" ? o : null;
  } catch {
    return null;
  }
};

// A category key used to colour a line. `obj` is the parsed event (or null);
// `raw` is the original string for the prose fallback.
export const classifyEvent = (obj, raw) => {
  if (obj && typeof obj.t === "string") {
    switch (obj.t) {
      case "nack":
      case "error":
        return "error";
      case "warning":
        return "warn";
      case "impact":
        return "impact";
      case "ack":
        return "ok";
      case "store":
        return obj.ev === "save_failed" ? "error" : "store";
      case "session":
        return "session";
      case "button":
        return "button";
      case "ble":
        return "ble";
      case "sync":
        return "sync";
      case "tx":
        return "tx";
      case "status":
      case "led":
      case "id":
      case "state":
      case "substate":
        return "meta";
      default:
        return "json";
    }
  }
  const s = String(raw == null ? "" : raw);
  if (/^\s*\[host\]\s*>/.test(s)) return "cmd";
  if (
    /\b(error|fail(ed)?|fault|refused|invalid|unable)\b/i.test(s) ||
    /"t"\s*:\s*"(nack|error)"/i.test(s)
  )
    return "error";
  if (/^\s*\[ble\]/i.test(s)) return "ble";
  if (/^\s*\[host\]/i.test(s)) return "host";
  return "default";
};

const toG = (mg) => (typeof mg === "number" ? (mg / 1000).toFixed(2) : null);

// A short, human summary of an event. Impact peaks are converted mg → g;
// everything else is rendered honestly as "t · ev · press → action (k=v …)".
// Returns null when there's nothing better than the raw line.
export const describeEvent = (o) => {
  if (!o || typeof o !== "object" || typeof o.t !== "string") return null;
  const t = o.t;

  if (t === "impact") {
    const head = [
      o.id != null ? `#${o.id}` : null,
      toG(o.peak_mg) != null ? `${toG(o.peak_mg)}g` : null,
      o.band || null,
    ]
      .filter(Boolean)
      .join(" ");
    const tail = [
      o.rise_ms != null ? `rise ${o.rise_ms}ms` : null,
      o.dur_ms != null ? `dur ${o.dur_ms}ms` : null,
      o.over != null ? `over ${o.over}` : null,
      // Dry-run pulse-width verdict (fw v0.6.2+). PASS is shown too, not just
      // the failures -- the whole point of the dry run is seeing the verdict
      // on every event so a bench session can be read off the terminal.
      // Absent on older firmware, which simply omits the field.
      o.gate != null ? `gate ${o.gate}` : null,
    ]
      .filter(Boolean)
      .join(" ");
    return `impact ${head}${tail ? ` · ${tail}` : ""}`.trim();
  }

  // Live mode tracking (v0.5.81+): one line per state/sub-phase transition.
  if (t === "state" && o.from && o.to) {
    return `state ${o.from} → ${o.to}`;
  }
  if (t === "substate" && o.sub) {
    return `substate · ${o.sub}${o.state ? ` (${o.state})` : ""}`;
  }

  if (t === "ack" || t === "nack") {
    return [
      t,
      o.cmd || null,
      o.state ? `(${o.state})` : null,
      o.reason ? `(${o.reason})` : null,
    ]
      .filter(Boolean)
      .join(" ");
  }

  // Radio transmit events: 0x10 impact, 0x11 load snippet, 0x12 status.
  if (t === "tx") {
    const g = (mg) => (typeof mg === "number" ? (mg / 1000).toFixed(1) : "?");
    if (o.pkt === "0x11") {
      let s = ["tx snippet", o.id != null ? `#${o.id}` : null]
        .filter(Boolean)
        .join(" · ");
      if (o.max_mg != null) s += ` · ${g(o.max_mg)}g peak`;
      s += " · 1.9s";
      if (o.min_mg != null && o.max_mg != null)
        s += ` (${o.min_mg}–${o.max_mg} mg)`;
      return s;
    }
    if (o.pkt === "0x10") {
      let s = ["tx impact", o.id != null ? `#${o.id}` : null]
        .filter(Boolean)
        .join(" · ");
      if (o.mag_mg != null) s += ` · ${g(o.mag_mg)}g`;
      // re-tx is redundancy; demo is fake — surface but don't hide.
      if (o.tag && o.tag !== "tx") s += ` [${o.tag}]`;
      if (o.x_mg != null)
        s += ` · x/y/z ${g(o.x_mg)}/${g(o.y_mg)}/${g(o.z_mg)}`;
      return s;
    }
    if (o.pkt === "0x12") {
      const bits = ["tx status"];
      if (o.impacts != null) bits.push(`${o.impacts} impacts`);
      if (o.bcasts != null) bits.push(`${o.bcasts} bcasts`);
      if (o.offline) bits.push(`${o.offline} offline`);
      if (o.batt_pct != null) bits.push(`batt ${o.batt_pct}%`);
      const fl = [];
      if (o.flags & 1) fl.push("charging");
      if (o.flags & 2) fl.push("battery_low");
      if (o.flags & 4) fl.push("imu_fault");
      return bits.join(" · ") + (fl.length ? ` · ${fl.join(" ")}` : "");
    }
    // Unknown pkt → fall through to the generic renderer.
  }

  // Generic: lead with the event kind, then the remaining scalar fields.
  const head = [t, o.ev, o.press].filter(Boolean).join(" · ");
  const arrow = o.action ? ` → ${o.action}` : o.char ? ` ${o.char}` : "";

  // Countdown fields (v0.5.70+): prefer the human duration / wall-clock and
  // drop their numeric twins. `at` is absent when the clock was never synced.
  const dur = o.starts_in || o.in || null; // "9m 13s"
  const at = o.starts_at || o.at || null; // "2026-08-10 15:30:00"
  const when = dur
    ? ` · in ${dur}${at ? ` (at ${at} UTC)` : ""}`
    : at
    ? ` · at ${at} UTC`
    : "";

  const skip = new Set(["t", "ev", "press", "action", "char"]);
  if (dur)
    ["starts_in", "in", "starts_in_s", "in_s"].forEach((k) => skip.add(k));
  if (at) ["starts_at", "at"].forEach((k) => skip.add(k));

  const facts = Object.entries(o)
    .filter(
      ([k, v]) => !skip.has(k) && v != null && v !== "" && typeof v !== "object"
    )
    .map(([k, v]) => `${k}=${v}`)
    .join(" ");
  return `${head}${arrow}${when}${facts ? ` (${facts})` : ""}`;
};

// Decode the 24 sample-bytes of a 0x11 load-snippet advert (raw.slice(10)) into
// 38 milli-g values. 5-bit samples, MSB-first, mg = raw5 * 10000 / 31. This is
// byte-for-byte the same decode the firmware applies, so asserting it against a
// live event's `samples_mg` keeps our BLE advert parser from silently drifting.
export const unpack5bit = (hex) => {
  const bytes = String(hex || "").match(/../g);
  if (!bytes) return [];
  let acc = 0n;
  let bits = 0n;
  const out = [];
  for (const b of bytes) {
    acc = (acc << 8n) | BigInt(parseInt(b, 16));
    bits += 8n;
    while (bits >= 5n && out.length < 38) {
      out.push(Number((acc >> (bits - 5n)) & 0x1fn));
      bits -= 5n;
    }
  }
  return out.map((v) => Math.floor((v * 10000) / 31));
};
